import { useMetaData } from './useMetaData.js'
import { useUpdateMeta } from '../mutations/useUpdateMeta.js'
import { useUIStore } from '../store/uiStore.js'

/**
 * useComments — read + mutate comment threads for a given context label.
 *
 * A thread is keyed by `contextLabel` (the goal or sub-goal name), matching
 * the current /q2-goals.html dashboard's storage format.
 *
 * Shape (from q2-goals.html line 3665):
 *   meta.comments = [{
 *     id, contextLabel, contextType, resolved,
 *     replies: [{ id, author, text, at, attachments? }]
 *   }]
 *
 * Pass `contextLabel = null` to read/mutate ALL threads (useful for a
 * global inbox view later).
 */
export function useComments(contextLabel) {
  const currentUser = useUIStore((s) => s.currentUser) || 'Anonymous'
  const { data: meta } = useMetaData()
  const updateMeta = useUpdateMeta()

  const all = Array.isArray(meta.comments) ? meta.comments : []
  const threads = contextLabel
    ? all.filter((t) => t.contextLabel === contextLabel)
    : all

  const newId = () => Math.random().toString(36).slice(2)

  const createThread = ({ text, contextType = '' }) => {
    if (!text?.trim() || !contextLabel) return
    const thread = {
      id: newId(),
      contextLabel,
      contextType,
      resolved: false,
      replies: [
        {
          id: newId(),
          author: currentUser,
          text: text.trim(),
          at: new Date().toISOString(),
        },
      ],
    }
    updateMeta.mutate((m) => ({
      ...m,
      comments: [...(m.comments || []), thread],
    }))
  }

  const addReply = (threadId, text) => {
    if (!text?.trim()) return
    const reply = {
      id: newId(),
      author: currentUser,
      text: text.trim(),
      at: new Date().toISOString(),
    }
    updateMeta.mutate((m) => ({
      ...m,
      comments: (m.comments || []).map((t) =>
        t.id === threadId ? { ...t, replies: [...(t.replies || []), reply] } : t
      ),
    }))
  }

  const setResolved = (threadId, resolved) => {
    updateMeta.mutate((m) => ({
      ...m,
      comments: (m.comments || []).map((t) =>
        t.id === threadId ? { ...t, resolved, resolvedAt: resolved ? new Date().toISOString() : null, resolvedBy: resolved ? currentUser : null } : t
      ),
    }))
  }

  const deleteThread = (threadId) => {
    updateMeta.mutate((m) => ({
      ...m,
      comments: (m.comments || []).filter((t) => t.id !== threadId),
    }))
  }

  // Stats for the bubble: count of open threads and total replies under them
  const openThreads = threads.filter((t) => !t.resolved)
  const openReplyCount = openThreads.reduce(
    (n, t) => n + (t.replies?.length || 0),
    0
  )

  return {
    threads,
    openThreads,
    openThreadCount: openThreads.length,
    openReplyCount,
    createThread,
    addReply,
    setResolved,
    deleteThread,
  }
}
